import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CategorySelect } from "@/components/categories/CategorySelect";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Mic, Square, X, Check, RotateCcw } from "lucide-react";
import { useIsPremium } from "@/components/PremiumGate";

export function openVoiceExpense() {
  window.dispatchEvent(new CustomEvent("voice-expense:open"));
}

export function onVoiceExpenseSaved(handler) {
  window.addEventListener("voice-expense:saved", handler);
  return () => window.removeEventListener("voice-expense:saved", handler);
}

export function VoiceMicButton({ testid, className, label = "Voice" }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={openVoiceExpense}
      className={className || "border border-[#1A1F16] px-4 py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#1A1F16] hover:text-[#F7F5F0] flex items-center gap-2"}
    >
      <Mic className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

/** Upload the recorded audio and get {transcript, draft?, error?} back. */
async function parseVoiceAudio(blob) {
  const fd = new FormData();
  fd.append("audio", blob, "voice.webm");
  const { data } = await api.post("/voice/parse-expense", fd);
  return data;
}

/** Build a MediaRecorder that collects chunks and hands a single Blob to onBlob when stopped. */
function createRecorder(stream, onBlob) {
  const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
  const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
  rec.onstop = () => onBlob(new Blob(chunks, { type: rec.mimeType || "audio/webm" }));
  return rec;
}

/**
 * State machine for browser voice capture + backend parse.
 * Phases: idle -> recording -> processing -> preview -> saving
 */
function useVoiceRecording() {
  const [phase, setPhase] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const recorderRef = useRef(null);
  const timerRef = useRef(null);

  const reset = useCallback(() => {
    setPhase("idle"); setSeconds(0); setTranscript(""); setDraft(null); setError(null);
  }, []);

  const stopTracks = () => {
    recorderRef.current?.stream?.getTracks().forEach((t) => t.stop());
  };

  const stopRecording = useCallback(() => {
    clearInterval(timerRef.current);
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  }, []);

  const sendAudio = useCallback(async (blob) => {
    setPhase("processing");
    try {
      const data = await parseVoiceAudio(blob);
      setTranscript(data.transcript || "");
      if (data.draft) {
        setDraft({ ...data.draft, amount: String(data.draft.amount) });
        setPhase("preview");
      } else {
        setError(data.error || "Could not understand — try again.");
        setPhase("idle");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Transcription failed — try again.");
      setPhase("idle");
    }
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = createRecorder(stream, (blob) => { stopTracks(); sendAudio(blob); });
      recorderRef.current = rec;
      rec.start();
      setPhase("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => {
        if (s >= 29) { stopRecording(); return s; }
        return s + 1;
      }), 1000);
    } catch {
      setError("Microphone access denied. Allow mic permission and try again.");
    }
  }, [sendAudio, stopRecording]);

  const teardown = useCallback(() => {
    stopRecording();
    stopTracks();
    reset();
  }, [reset, stopRecording]);

  return { phase, setPhase, seconds, transcript, draft, setDraft, error, reset, startRecording, stopRecording, teardown };
}

function RecordPhase({ phase, seconds, onStart, onStop }) {
  const recording = phase === "recording";
  return (
    <div className="flex flex-col items-center py-6">
      <button
        data-testid={recording ? "voice-stop-btn" : "voice-record-btn"}
        onClick={recording ? onStop : onStart}
        className={`w-24 h-24 rounded-full flex items-center justify-center transition-colors ${
          recording ? "bg-[#D94F3D] text-white animate-pulse" : "bg-[#1A1F16] text-[#F7F5F0] hover:bg-[#2C3628]"
        }`}
      >
        {recording ? <Square className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
      </button>
      <div className="font-mono text-xs uppercase tracking-widest text-[#6C7565] mt-4" data-testid="voice-status">
        {recording ? `Listening… ${seconds}s (tap to stop)` : "Tap to record"}
      </div>
      <p className="text-xs text-[#6C7565] mt-3 text-center max-w-xs italic">
        Speak in English — e.g. "Spent 450 on groceries at BigBasket yesterday" or "Got 5000 freelance payment"
      </p>
    </div>
  );
}

function ProcessingPhase() {
  return (
    <div className="flex flex-col items-center py-10" data-testid="voice-processing">
      <div className="w-10 h-10 border-2 border-[#1A1F16] border-t-transparent rounded-full animate-spin" />
      <div className="font-mono text-xs uppercase tracking-widest text-[#6C7565] mt-4">Transcribing with Whisper…</div>
    </div>
  );
}

function PreviewPhase({ transcript, draft, setDraft, saving, onRedo, onConfirm }) {
  return (
    <div data-testid="voice-preview">
      <blockquote className="border-l-2 border-[#A67C00] pl-3 text-sm italic text-[#3C4537] mb-5" data-testid="voice-transcript">
        "{transcript}"
      </blockquote>
      <div className="flex gap-2 mb-4">
        {["expense", "income"].map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`voice-type-${t}`}
            onClick={() => setDraft({ ...draft, type: t, category: "Other" })}
            className={`flex-1 py-2 text-[10px] uppercase tracking-widest border ${draft.type === t ? "bg-[#1A1F16] text-[#F7F5F0] border-[#1A1F16]" : "border-[#1A1F16]"}`}
          >{t}</button>
        ))}
      </div>
      <label className="block mb-3">
        <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Amount (₹)</span>
        <input type="number" step="0.01" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} data-testid="voice-amount" className="w-full bg-transparent border-b border-[#1A1F16] py-2 font-serif text-2xl num focus:outline-none focus:border-[#A67C00]" />
      </label>
      <label className="block mb-3">
        <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Category</span>
        <CategorySelect type={draft.type} value={draft.category} onChange={(c) => setDraft({ ...draft, category: c })} testid="voice-category" />
      </label>
      <label className="block mb-3">
        <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Description</span>
        <input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="voice-description" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none" />
      </label>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565] mb-5">Date: {formatDate(draft.date)}</div>
      <div className="flex gap-3">
        <button data-testid="voice-retry-btn" onClick={onRedo} className="border border-[#1A1F16] px-4 py-3 text-xs uppercase tracking-widest hover:bg-[#EAE7DD] flex items-center gap-2">
          <RotateCcw className="w-3.5 h-3.5" /> Redo
        </button>
        <button
          data-testid="voice-confirm-btn"
          onClick={onConfirm}
          disabled={saving || !draft.amount}
          className="flex-1 bg-[#1A1F16] text-[#F7F5F0] py-3 text-xs uppercase tracking-widest hover:bg-[#2C3628] disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Check className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Confirm & record"}
        </button>
      </div>
    </div>
  );
}

export function VoiceExpenseWidget() {
  const [open, setOpen] = useState(false);
  const isPremium = useIsPremium();
  const navigate = useNavigate();
  const voice = useVoiceRecording();
  const { phase, setPhase, seconds, transcript, draft, setDraft, error, reset, startRecording, stopRecording, teardown } = voice;

  const tryOpen = useCallback(() => {
    if (!isPremium) {
      toast("Voice logging is a Premium feature", { description: "Upgrade to record expenses by speaking." });
      navigate("/pricing");
      return;
    }
    setOpen(true);
    reset();
  }, [isPremium, navigate, reset]);

  useEffect(() => {
    window.addEventListener("voice-expense:open", tryOpen);
    return () => window.removeEventListener("voice-expense:open", tryOpen);
  }, [tryOpen]);

  const save = async () => {
    setPhase("saving");
    try {
      await api.post("/transactions", {
        type: draft.type,
        amount: parseFloat(draft.amount),
        category: draft.category,
        description: draft.description,
        date: draft.date,
      });
      toast.success("Transaction recorded");
      window.dispatchEvent(new CustomEvent("voice-expense:saved"));
      close();
    } catch {
      toast.error("Failed to save");
      setPhase("preview");
    }
  };

  const close = () => {
    teardown();
    setOpen(false);
  };

  return (
    <>
      {/* Floating mic button */}
      <button
        data-testid="voice-fab"
        onClick={tryOpen}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#1A1F16] text-[#F7F5F0] flex items-center justify-center shadow-lg hover:bg-[#2C3628] transition-colors"
        title="Log by voice"
      >
        <Mic className="w-5 h-5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#1A1F16]/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={close} data-testid="voice-modal-overlay">
          <div className="bg-[#F7F5F0] border border-[#1A1F16] w-full max-w-md p-8 relative" onClick={(e) => e.stopPropagation()} data-testid="voice-modal">
            <button data-testid="voice-modal-close" onClick={close} className="absolute top-4 right-4 text-[#6C7565] hover:text-[#1A1F16]"><X className="w-4 h-4" /></button>
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#A67C00]">Voice entry</span>
            <h3 className="font-serif text-3xl tracking-tight mt-1 mb-6">Speak your expense.</h3>

            {error && <div className="border border-[#D94F3D] text-[#D94F3D] text-sm p-3 mb-4" data-testid="voice-error">{error}</div>}

            {(phase === "idle" || phase === "recording") && (
              <RecordPhase phase={phase} seconds={seconds} onStart={startRecording} onStop={stopRecording} />
            )}
            {phase === "processing" && <ProcessingPhase />}
            {(phase === "preview" || phase === "saving") && draft && (
              <PreviewPhase transcript={transcript} draft={draft} setDraft={setDraft} saving={phase === "saving"} onRedo={reset} onConfirm={save} />
            )}
          </div>
        </div>
      )}
    </>
  );
}
